// src/lib/onesignal.ts
// OneSignal Web Push Notification Service

import { getCurrentUser } from './auth';
import { supabase } from './supabase';

// OneSignal App ID - Replace with your actual OneSignal App ID
const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID || '';

/**
 * Detect if running inside Android WebView (WebViewGold app)
 */
const isAndroidWebView = (): boolean => {
  return !!(window as any).AndroidOneSignal;
};

/**
 * Get Android native OneSignal bridge if available
 */
const getAndroidBridge = (): any => {
  return (window as any).AndroidOneSignal;
};

// Check if OneSignal is available
const getOneSignal = (): any => {
  return (window as any).OneSignalDeferred || (window as any).OneSignal;
};

/**
 * Initialize OneSignal
 */
export const initOneSignal = async (): Promise<void> => {
  // In Android WebView, the native OneSignal SDK handles everything
  if (isAndroidWebView()) {
    console.log('[OneSignal] Running inside Android WebView - native SDK handles push');
    // Link user if already logged in
    const user = getCurrentUser();
    if (user?.id) {
      await setExternalUserId(user.id);
    }
    return;
  }

  if (!ONESIGNAL_APP_ID) {
    console.warn('OneSignal App ID not configured');
    return;
  }

  // Wait for OneSignal to be ready
  const OneSignalDeferred = (window as any).OneSignalDeferred || [];
  (window as any).OneSignalDeferred = OneSignalDeferred;

  OneSignalDeferred.push(async function(OneSignal: any) {
    try {
      await OneSignal.init({
        appId: ONESIGNAL_APP_ID,
        safari_web_id: import.meta.env.VITE_ONESIGNAL_SAFARI_WEB_ID || undefined,
        notifyButton: {
          enable: false, // We'll use our own UI
        },
        allowLocalhostAsSecureOrigin: true, // For development
        serviceWorkerParam: {
          scope: '/'
        },
        serviceWorkerPath: '/OneSignalSDKWorker.js',
        promptOptions: {
          slidedown: {
            prompts: [
              {
                type: "push",
                autoPrompt: false, // We'll trigger manually
                text: {
                  actionMessage: "Stay updated with friend requests, messages, and new content!",
                  acceptButton: "Allow",
                  cancelButton: "Later"
                },
                delay: {
                  pageViews: 1,
                  timeDelay: 5
                }
              }
            ]
          }
        }
      });

      console.log('OneSignal initialized successfully');

      // Set external user ID if user is logged in
      const user = getCurrentUser();
      if (user?.id) {
        await setExternalUserId(user.id);
      }
    } catch (error) {
      console.error('Error initializing OneSignal:', error);
    }
  });
};

/**
 * Set external user ID for targeting specific users
 */
export const setExternalUserId = async (userId: string): Promise<void> => {
  // Use native Android bridge if available
  if (isAndroidWebView()) {
    try {
      const bridge = getAndroidBridge();
      bridge.login(userId);
      bridge.addTag('user_id', userId);
      console.log('[OneSignal] Native Android login with user ID:', userId);
    } catch (err) {
      console.error('[OneSignal] Error in native Android login:', err);
    }
    return;
  }

  if (!ONESIGNAL_APP_ID || ONESIGNAL_APP_ID === 'YOUR_ONESIGNAL_APP_ID') {
    console.warn('[OneSignal] App ID not configured, skipping external user ID');
    return;
  }

  try {
    const OneSignalDeferred = (window as any).OneSignalDeferred || [];
    (window as any).OneSignalDeferred = OneSignalDeferred;

    OneSignalDeferred.push(async function(OneSignal: any) {
      try {
        // Check if already logged in with this user ID
        const currentExternalId = await OneSignal.User?.getExternalId?.();
        if (currentExternalId === userId) {
          console.log('[OneSignal] User already logged in with ID:', userId);
          return;
        }

        await OneSignal.login(userId);
        console.log('[OneSignal] External user ID set successfully:', userId);
        
        // Also add user ID as a tag for backup targeting
        await OneSignal.User?.addTags?.({ user_id: userId });
      } catch (err) {
        console.error('[OneSignal] Error in login:', err);
      }
    });
  } catch (error) {
    console.error('[OneSignal] Error setting external user ID:', error);
  }
};

/**
 * Remove external user ID on logout
 */
export const removeExternalUserId = async (): Promise<void> => {
  // Use native Android bridge if available
  if (isAndroidWebView()) {
    try {
      const bridge = getAndroidBridge();
      bridge.logout();
      console.log('[OneSignal] Native Android logout');
    } catch (err) {
      console.error('[OneSignal] Error in native Android logout:', err);
    }
    return;
  }

  if (!ONESIGNAL_APP_ID || ONESIGNAL_APP_ID === 'YOUR_ONESIGNAL_APP_ID') {
    return;
  }

  try {
    const OneSignalDeferred = (window as any).OneSignalDeferred || [];
    (window as any).OneSignalDeferred = OneSignalDeferred;

    OneSignalDeferred.push(async function(OneSignal: any) {
      try {
        await OneSignal.logout();
        console.log('[OneSignal] External user ID removed');
      } catch (err) {
        console.error('[OneSignal] Error in logout:', err);
      }
    });
  } catch (error) {
    console.error('[OneSignal] Error removing external user ID:', error);
  }
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  // In Android WebView, the native SDK handles permissions
  if (isAndroidWebView()) {
    console.log('[OneSignal] Android WebView - native SDK handles notification permission');
    return true; // Permission is handled natively
  }

  try {
    const OneSignal = getOneSignal();
    if (!OneSignal) return false;

    return new Promise((resolve) => {
      OneSignal.push(async function() {
        const permission = await OneSignal.Notifications.requestPermission();
        console.log('OneSignal permission:', permission);
        resolve(permission);
      });
    });
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Show the native permission prompt
 */
export const showPermissionPrompt = async (): Promise<void> => {
  // In Android WebView, skip the web prompt
  if (isAndroidWebView()) return;

  try {
    const OneSignal = getOneSignal();
    if (!OneSignal) return;

    OneSignal.push(async function() {
      await OneSignal.Slidedown.promptPush();
    });
  } catch (error) {
    console.error('Error showing permission prompt:', error);
  }
};

/**
 * Check if notifications are enabled
 */
export const isNotificationsEnabled = async (): Promise<boolean> => {
  // In Android WebView, the native SDK manages this
  if (isAndroidWebView()) {
    return true; // Assume enabled since native SDK handles it
  }

  try {
    const OneSignal = getOneSignal();
    if (!OneSignal) return false;

    return new Promise((resolve) => {
      OneSignal.push(async function() {
        const permission = await OneSignal.Notifications.permission;
        resolve(permission);
      });
    });
  } catch (error) {
    console.error('Error checking notification permission:', error);
    return false;
  }
};

/**
 * Get OneSignal subscription ID (Player ID)
 */
export const getPlayerId = async (): Promise<string | null> => {
  try {
    const OneSignal = getOneSignal();
    if (!OneSignal) return null;

    return new Promise((resolve) => {
      OneSignal.push(async function() {
        const playerId = await OneSignal.User.PushSubscription.id;
        resolve(playerId || null);
      });
    });
  } catch (error) {
    console.error('Error getting OneSignal player ID:', error);
    return null;
  }
};

/**
 * Add tags for user segmentation
 */
export const addTags = async (tags: Record<string, string>): Promise<void> => {
  try {
    const OneSignal = getOneSignal();
    if (!OneSignal) return;

    OneSignal.push(async function() {
      await OneSignal.User.addTags(tags);
      console.log('OneSignal tags added:', tags);
    });
  } catch (error) {
    console.error('Error adding OneSignal tags:', error);
  }
};

/**
 * Check if OneSignal is properly configured
 */
export const isOneSignalConfigured = (): boolean => {
  const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
  return !!(appId && appId !== 'YOUR_ONESIGNAL_APP_ID');
};

/**
 * Send push notification to specific user via OneSignal REST API
 * Note: This should ideally be done from a backend/edge function for security
 */
const sendPushToExternalIds = async (
  externalIds: string[],
  title: string,
  message: string,
  url?: string,
  data?: Record<string, any>
): Promise<boolean> => {
  if (!ONESIGNAL_APP_ID || ONESIGNAL_APP_ID === 'YOUR_ONESIGNAL_APP_ID') {
    console.warn('OneSignal App ID not configured');
    return false;
  }

  try {
    const { data: response, error } = await supabase.functions.invoke('onesignal-send', {
      body: {
        externalIds,
        title,
        message,
        url,
        data,
      },
    });

    if (error) {
      console.error('[OneSignal] Edge function error:', error);
      return false;
    }

    if (!response?.success) {
      console.error('[OneSignal] Edge function failed:', response?.error || 'Unknown error');
      return false;
    }

    if (response?.recipients === 0) {
      console.warn('[OneSignal] No recipients found for external_id:', externalIds);
      return false;
    }

    return true;
  } catch (error) {
    console.error('[OneSignal] Error sending push notification:', error);
    return false;
  }
};

export const sendPushNotification = async (
  externalUserId: string,
  title: string,
  message: string,
  url?: string,
  data?: Record<string, any>
): Promise<boolean> => {
  return sendPushToExternalIds([externalUserId], title, message, url, data);
};

/**
 * Notification types for the app
 */
export type PushNotificationType = 
  | 'friend_request'
  | 'friend_accepted'
  | 'new_message'
  | 'new_song'
  | 'new_story';

const ALLOWED_NOTIFICATION_ORIGINS = [
  'https://learn.amah-llc.com',
  'https://amahly.com',
];

const getNotificationBaseUrl = (): string => {
  const origin = window.location.origin;
  const isLocalhost =
    origin.startsWith('http://localhost') || origin.startsWith('http://127.0.0.1');
  if (isLocalhost) return origin;
  if (ALLOWED_NOTIFICATION_ORIGINS.includes(origin)) return origin;
  return ALLOWED_NOTIFICATION_ORIGINS[0];
};

/**
 * Get notification URL based on type
 */
const getNotificationUrl = (type: PushNotificationType, relatedId?: string): string => {
  const baseUrl = getNotificationBaseUrl();
  
  switch (type) {
    case 'friend_request':
      return `${baseUrl}/learners`;
    case 'friend_accepted':
      return `${baseUrl}/friends`;
    case 'new_message':
      return `${baseUrl}/messages`;
    case 'new_song':
      return relatedId ? `${baseUrl}/songs/${relatedId}` : `${baseUrl}/songs`;
    case 'new_story':
      return relatedId ? `${baseUrl}/story/${relatedId}` : `${baseUrl}/stories`;
    default:
      return `${baseUrl}/dashboard`;
  }
};

/**
 * Send notification for friend request
 */
export const sendFriendRequestPush = async (
  receiverId: string,
  senderName: string
): Promise<void> => {
  await sendPushNotification(
    receiverId,
    'New Friend Request',
    `${senderName} sent you a friend request`,
    getNotificationUrl('friend_request'),
    { type: 'friend_request' }
  );
};

/**
 * Send notification for friend request accepted
 */
export const sendFriendAcceptedPush = async (
  receiverId: string,
  accepterName: string
): Promise<void> => {
  await sendPushNotification(
    receiverId,
    'Friend Request Accepted',
    `${accepterName} accepted your friend request`,
    getNotificationUrl('friend_accepted'),
    { type: 'friend_accepted' }
  );
};

/**
 * Send notification for new message
 */
export const sendNewMessagePush = async (
  receiverId: string,
  senderName: string
): Promise<void> => {
  await sendPushNotification(
    receiverId,
    'New Message',
    `You received a new message from ${senderName}`,
    getNotificationUrl('new_message'),
    { type: 'new_message' }
  );
};

/**
 * Send notification for new song (to multiple users)
 */
export const sendNewSongPush = async (
  userIds: string[],
  songTitle: string,
  songId: string
): Promise<void> => {
  await sendPushToExternalIds(
    userIds,
    'New Song Available',
    `New song "${songTitle}" has been added!`,
    getNotificationUrl('new_song', songId),
    { type: 'new_song', songId }
  );
};

/**
 * Send notification for new story (to multiple users)
 */
export const sendNewStoryPush = async (
  userIds: string[],
  storyTitle: string,
  storyId: string
): Promise<void> => {
  await sendPushToExternalIds(
    userIds,
    'New Story Available',
    `New story "${storyTitle}" has been added!`,
    getNotificationUrl('new_story', storyId),
    { type: 'new_story', storyId }
  );
};
